/**
 * Checkout diagnostics. A failed submission used to be mapped onto fields and then discarded,
 * which left nothing to look at when a customer reported "I got an error" — and the plugin has
 * no server side to log to (by design: it never proxies the API).
 *
 * Errors are always logged to the browser console with field KEYS and status only — never field
 * values, so a screenshot of the console carries no personal data. `?cbf_debug=1` adds the phase
 * trail, the API's own messages and the request body; that stays in the customer's own browser.
 */
export interface Logger {
  /** Always logged. `detail` must contain no personal data. */
  error(message: string, detail?: Record<string, unknown>): void;
  /** Only with ?cbf_debug=1. May contain the customer's own data. */
  debug(message: string, detail?: Record<string, unknown>): void;
  readonly verbose: boolean;
}

export function createLogger(
  verbose = false,
  sink: Pick<Console, 'error' | 'info'> = console,
): Logger {
  return {
    verbose,
    error(message, detail) {
      sink.error(`[cbf] ${message}`, detail ?? '');
    },
    debug(message, detail) {
      if (verbose) sink.info(`[cbf] ${message}`, detail ?? '');
    },
  };
}

/**
 * One-line summary safe to log unconditionally: status, the step it failed at and the API's own
 * field KEYS (never values). Browsers collapse a detail object to "Object", so the essentials
 * belong in the message itself — that line is what someone pastes into a bug report.
 */
export function summariseApiFailure(err: unknown, phase?: string): string {
  const e = err as { status?: number; fieldErrors?: Record<string, string[]>; name?: string };
  const status = typeof e?.status === 'number' ? String(e.status) : (e?.name ?? 'error');
  const keys = e?.fieldErrors ? Object.keys(e.fieldErrors) : [];
  const where = phase && phase !== 'idle' ? ` at ${phase}` : '';
  return `${status}${where}${keys.length ? ` — fields: ${keys.join(', ')}` : ' — no field errors'}`;
}

/** Shape of an API failure that is safe to log unconditionally. */
export function describeApiFailure(err: unknown): Record<string, unknown> {
  const e = err as {
    name?: string;
    status?: number;
    fieldErrors?: Record<string, string[]>;
    message?: string;
  };
  const detail: Record<string, unknown> = { type: e?.name ?? typeof err };
  if (typeof e?.status === 'number') detail['status'] = e.status;
  // keys only: values can hold the customer's address or notes
  if (e?.fieldErrors && Object.keys(e.fieldErrors).length)
    detail['fields'] = Object.keys(e.fieldErrors);
  return detail;
}
